import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CUEServiceBase, LiveServiceFile, LiveServiceFileCollection, LiveServiceFormDataBase } from './cue-base.service';
import { CUEServiceListViewBase } from '../_shared/base.management.component';
import { getUrl } from '../_shared/utils';
import { Service } from '../_models/Service';
import { JWTTokenService } from './jwtToken.service';

export interface TriviaResult {
  triviaGameId: number;
  date: string;
  numberOfPlayers: number;
  numberOfWinners: number;
  serviceLogId: number;
}

export interface ServiceLog {
  serviceLogId: number;
  resultJson: string;
  dateCreated: string;
  serviceId: number;
  service: Service;
}

export interface WinnerInfo {
  deviceId: string;
  еmail: string;
  name: string;
  phone: string;
  rank: number;
  score: number;
}

@Injectable()
export class TriviaService extends CUEServiceBase<TriviaGameFormData, TriviaGameListView> {
  constructor(http: HttpClient, jwtTokenService: JWTTokenService) {
    super(http, 'trivia', jwtTokenService);
  }

  public getServiceDisplayName(): string {
    return 'Trivia Game';
  }

  public getTriviaResultPlayers(triviaGameId: number, serviceLogId: number) {
    return this.httpClient.get<WinnerInfo[]>(getUrl(`trivia/results/${triviaGameId}/${serviceLogId}`), this.requestOptionsAuthenticated());
  }

  public getTriviaResultCSV(triviaId: number, serviceLogId: number) {
    const options = this.requestOptionsAuthenticated();

    options['responseType'] = 'blob';

    return this.httpClient.get<Blob>(getUrl(`trivia/results/${triviaId}/${serviceLogId}/download`), options);
  }

  public getTriviaResultsList(triviaGameId: number) {
    return this.httpClient.get<TriviaResult[]>(getUrl(`trivia/results/${triviaGameId}`), this.requestOptionsAuthenticated());
  }

  private requestOptionsAuthenticated() {
    const requestOptions = {
      headers: new HttpHeaders()
        .set('Content-type', 'application/json')
        .append('Authorization', 'Bearer ' + this.jwtTokenService.getToken('portal')),
    };
    return requestOptions;
  }
}

export interface TriviaGameListView extends CUEServiceListViewBase {
  downloadPending: number;
  gamesPlayed: number;
}

export class TriviaGameFormData implements LiveServiceFormDataBase {
  name: string;
  couponId: number;
  gameTitle: string;
  startDate: string;
  endDate: string;
  imageUrl: string;
  questions: Question[];
  timeToAnswer: number;
  numberOfWinners: number;
  rulesDisplayDuration: number;
  winnerText: string;
  loserText: string;
  navBarColor: string;
  selectedAnswerColor: string;
  listOfEmails: string[];
  winnerCouponId: number;
  loserCouponId: number;
  winnerUrl: string;
  loserUrl: string;
  useAudible: boolean;
  regenerateAudio: boolean;
  insertionPattern: string;
  embedMusic: boolean;
  embedMp3Url: string;
  embedPosition: number;
  showRules: boolean;
  title: string;
  rule1: string;
  rule2: string;
  rule3: string;
  startsInLabelText: string;
  dateCreated: Date;
  serviceFiles: TriviaServiceFileCollection;
}

export class TriviaServiceFileCollection implements LiveServiceFileCollection {
  files: TriviaServiceFile[];

  constructor() {

    this.files = [];
  }

  public addBrandingImageFile(file: File) {

    if (file) {

      const brandingImageIndex = this.files?.findIndex(f => f.fileType === TriviaServiceFileType.Image);

      if (brandingImageIndex > -1) {

        this.files[brandingImageIndex].file = file;

      } else {

        this.files?.push({ file, fileType: TriviaServiceFileType.Image });
      }
    }
  }

  public removeBrandingImageFile() {

    const brandingImageIndex = this.files?.findIndex(f => f.fileType === TriviaServiceFileType.Image);

    if (brandingImageIndex > -1) {

      this.files.splice(brandingImageIndex, 1);
    }
  }

  public addAudio(file: File) {

    if (file) {

      const trackFileIndex = this.files?.findIndex(f => f.fileType === TriviaServiceFileType.Audio);

      if (trackFileIndex > -1) {

        this.files[trackFileIndex].file = file;

      } else {

        this.files?.push({ file, fileType: TriviaServiceFileType.Audio});

      }
    }
  }
}

export class TriviaServiceFile implements LiveServiceFile {
  file: File;
  fileType: TriviaServiceFileType
}

export enum TriviaServiceFileType {
  Image,
  Audio,
}

export interface Question {
  question: string;
  answerOne: string;
  answerTwo: string;
  answerThree: string;
  answerFour: string;
  correctAnswer: number;
}
